import React, { useState, useEffect, useRef } from "react";
import { MapContainer, Marker, Tooltip, ZoomControl } from "react-leaflet";
import L from "leaflet";
import StationPinCNG from "../../assets/img/Station_Pin__CNG.svg";
import StationPinLNG from "../../assets/img/Station_Pin__LNG.svg";
import StationPinCNGLNG from "../../assets/img/Station_Pin__CNG_LNG.svg";
import StationPinFutureSite from "../../assets/img/Station_Pin__Future Site.svg";
import StationPinMaintenance from "../../assets/img/Station_Pin__Maintenance.svg";

import "leaflet/dist/leaflet.css";
import "./style.scss";
import { filterServerData, fetchStations } from "../../lib/helper";
import Routing from "../../components/TripPlanner/routing";
import { MARKERS } from "../../assets/markers/markers";
import GoogleMapsLayer from "../GoogleMapsComponent";

const LocationMarker = ({ position }) => {
  // Set a custom marker icon (optional)
  const customIcon = new L.Icon({
    iconUrl: MARKERS[0],
    iconSize: [60, 60],
    iconAnchor: [30, 60],
  });

  return position === null ? null : (
    <Marker position={position} icon={customIcon}>
      <Tooltip>You are here</Tooltip>
    </Marker>
  );
};

const SearchLocationMarker = ({ position }) => {
  // Set a custom marker icon with "A" for searched locations
  const searchIcon = new L.Icon({
    iconUrl: MARKERS[0], // Using "A" icon (first marker)
    iconSize: [60, 60],
    iconAnchor: [30, 60],
  });

  // Validate position before rendering
  if (
    !position ||
    typeof position.lat !== "number" ||
    typeof position.lng !== "number"
  ) {
    return null;
  }

  return (
    <Marker position={[position.lat, position.lng]} icon={searchIcon}>
      <Tooltip>Searched location</Tooltip>
    </Marker>
  );
};

const MapComponent = ({
  isCurrentLocation,
  filterValues,
  center,
  onStationSelect,
  onOpenModal,
  position,
  sourceCity,
  destinationCity,
  setRouteDetails,
  routeDetails,
  setSourceCity,
  destinations,
  mapView,
  buildTrip,
  clearTrip,
  setClearTrip,
  isTrafficViewEnabled,
}) => {
  const [stations, setStations] = useState([]);
  const [mapState, setMapState] = useState({
    center: center || [40.7128, -74.006], // Default to New York if no center provided
    zoom: 10, // Default zoom level
  });

  const [filterStation, setFilterStation] = useState([]);
  const [hasCenteredToLocation, setHasCenteredToLocation] = useState(false);

  const mapRef = useRef(null);

  // Load stations from API on component mount
  useEffect(() => {
    const loadStations = async () => {
      try {
        console.log("🔄 Loading stations from API...");
        const stationsData = await fetchStations();
        console.log("✅ Stations loaded:", stationsData.length, "stations");
        console.log("📊 Sample station:", stationsData[0]);
        setStations(stationsData);
      } catch (error) {
        console.error("❌ Failed to load stations:", error);
        setStations([]);
      }
    };

    loadStations();
  }, []);

  const createIcon = (iconUrl) => {
    return L.icon({
      iconUrl,
      iconSize: [25, 35],
      iconAnchor: [12, 35],
      popupAnchor: [0, -30],
    });
  };

  const getPin = (station) => {
    const status = station?.StationStatus?.toLowerCase?.() ?? "";

    // Status-based pins: grey for Under maintenance, blue for Coming soon
    if (status === "under maintenance") {
      return createIcon(StationPinMaintenance);
    }
    if (status === "coming soon") {
      return createIcon(StationPinFutureSite);
    }

    // Active/operational stations: fuel-type pins (green)
    if (
      station?.StationFuelTypeCNG === "Yes" &&
      station?.StationFuelTypeLNG === "No"
    ) {
      return createIcon(StationPinCNG);
    }
    if (
      station?.StationFuelTypeLNG === "Yes" &&
      station?.StationFuelTypeCNG === "No"
    ) {
      return createIcon(StationPinLNG);
    }
    if (
      station?.StationFuelTypeCNG === "Yes" &&
      station?.StationFuelTypeLNG === "Yes"
    ) {
      return createIcon(StationPinCNGLNG);
    }
    return false;
  };

  const handleStationClick = (station) => {
    if (onStationSelect) {
      onOpenModal(true);
      onStationSelect(station);
    }
  };

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      map.on("moveend", () => {
        setMapState({
          center: map.getCenter(),
          zoom: map.getZoom(),
        });
      });

      // Remove Leaflet attribution control
      if (map.attributionControl) {
        map.removeControl(map.attributionControl);
      }
    }
  }, []);

  // Remove attribution controls after map is fully loaded
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Remove Leaflet attribution control
    if (map.attributionControl) {
      map.removeControl(map.attributionControl);
    }

    // Remove Google Maps attribution elements via DOM manipulation
    const removeGoogleAttribution = () => {
      const attributionElements = document.querySelectorAll(
        '.gm-style-cc, .gmnoprint, .leaflet-control-attribution, a[href*="terms_maps"], a[aria-label*="Terms"]'
      );
      attributionElements.forEach((el) => {
        if (el && el.parentNode) {
          el.style.display = "none";
          el.style.visibility = "hidden";
        }
      });
    };

    // Remove immediately
    removeGoogleAttribution();

    // Also check periodically for dynamically added elements (Google Maps adds them after load)
    const intervalId = setInterval(removeGoogleAttribution, 1000);

    // Clean up after 10 seconds (should be enough for Google Maps to load)
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
    }, 10000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure map state is initialized with proper center
  useEffect(() => {
    if (
      !mapState.center ||
      (mapState.center[0] === 0 && mapState.center[1] === 0)
    ) {
      setMapState({
        center: [40.7128, -74.006], // New York coordinates
        zoom: 10,
      });
    }
  }, [mapState.center]);

  useEffect(() => {
    console.log(
      "🔍 Filtering stations...",
      "Stations:",
      stations.length,
      "Filter values:",
      filterValues
    );
    const filteredStations = filterServerData(filterValues, stations);
    console.log("✅ Filtered stations:", filteredStations.length);
    setFilterStation(filteredStations);
  }, [filterValues, stations]);

  // Update map center when center prop changes (from search)
  useEffect(() => {
    if (center && mapRef.current) {
      const map = mapRef.current;
      map.setView([center.lat, center.lng], 12);
      setMapState({
        center: center,
        zoom: 12,
      });
    }
  }, [center]);

  // Center the map only once when current location is first obtained
  useEffect(() => {
    if (
      position &&
      isCurrentLocation &&
      !hasCenteredToLocation &&
      mapRef.current &&
      !center // Don't center to current location if there's a search center
    ) {
      const map = mapRef.current;
      map.setView([position.lat, position.lng], 12);
      setMapState({
        center: position,
        zoom: 12,
      });
      setHasCenteredToLocation(true);
    }
  }, [position, isCurrentLocation, hasCenteredToLocation, center]);

  // Reset the centering flag when isCurrentLocation becomes false
  useEffect(() => {
    if (!isCurrentLocation) {
      setHasCenteredToLocation(false);
    }
  }, [isCurrentLocation]);

  return (
    <MapContainer
      center={mapState.center || [40.7128, -74.006]} // Default to New York if no center
      zoom={mapState.zoom} // Preserve zoom level
      zoomControl={false}
      style={{ width: "100%", height: "100%" }}
      ref={mapRef}
      zoomAnimation={true} // Enables smooth zoom
      zoomAnimationThreshold={4} // Adjust this for better performance
      fadeAnimation={true} // Ensures tiles fade in smoothly
      markerZoomAnimation={true} // Keeps markers smooth while zooming
    >
      <ZoomControl position="bottomright" sx={{ color: "#08652e" }} />

      <GoogleMapsLayer
        type={mapView}
        isTrafficViewEnabled={isTrafficViewEnabled}
      />

      {position?.lat && position?.lng && isCurrentLocation && (
        <LocationMarker position={position} />
      )}

      {/* Show search location marker when location is searched (not current location) */}
      {!isCurrentLocation && center?.lat && center?.lng && (
        <SearchLocationMarker position={center} />
      )}

      {filterStation?.map((station, index) => {
        const lat = parseFloat(station?.Latitude);
        const lng = parseFloat(station?.Longitude);
        if (isNaN(lat) || isNaN(lng)) {
          console.error(
            `Invalid coordinates for station: ${station?.StationIdNumber}`
          );
          return null;
        }

        const pin = getPin(station);
        console.log(
          `📍 Station ${index}: ${station?.StationCity}, ${station?.StationState} - Pin:`,
          pin ? "✅" : "❌"
        );

        return (
          pin && (
            <Marker
              key={index}
              position={[lat, lng]}
              icon={pin}
              eventHandlers={{
                click: () => {
                  handleStationClick(station);
                },
              }}
            ></Marker>
          )
        );
      })}
      {/* <LocationMarker position={position} /> */}
      {/* <ZoomControlBtn /> */}
      <Routing
        sourceCity={sourceCity}
        destinationCity={destinationCity}
        setSourceCity={setSourceCity}
        destinations={destinations}
        setRouteDetails={setRouteDetails}
        buildTrip={buildTrip} // <-- Add this
        clearTrip={clearTrip}
      />
    </MapContainer>
  );
};

export default MapComponent;
